"use client";

import { usePluralTranslator, useTranslator } from "@/lib/translation-context";

import styles from "./_travelers-summary.module.css";

export interface TravelerSummaryRow {
  id: string;
  /** Category name as the operator wrote it (e.g. "Couple"). */
  label: string;
  /** How many of this category the traveller picked — the stepper's number. */
  count: number;
  /** People those picks add up to. Differs from `count` for per-unit and
   *  per-group categories, and is 0 for a flat fee that carries nobody. */
  pax: number;
}

/**
 * The party, itemised — shown when the collapsed picker's one-line breakdown
 * runs out of room. Deliberately NOT the traveller picker: this is a read-only
 * statement of what is currently selected, with no steppers to mis-click while
 * reaching for it, and it says the thing the one-liner cannot — that "Couple
 * x 6" is twelve people.
 */
export function TravelersSummary({
  rows,
  total,
}: {
  rows: TravelerSummaryRow[];
  total: number;
}): React.ReactElement {
  const t = useTranslator();
  const tn = usePluralTranslator();

  return (
    <div className={styles.summary}>
      <ul className={styles.rows}>
        {rows.map((row) => (
          <li key={row.id} className={styles.row}>
            <span>
              {row.label}
              <span className={styles.count}>×{row.count}</span>
            </span>
            {/* Stated on every row, including the per-person ones where it
                matches the count. A column that appears on some rows and not
                others reads as "these are the special ones" and invites the
                traveller to work out why, when the honest answer is that all
                of them resolve to a head count. */}
            <span className={styles.pax}>
              {row.pax === 0
                ? t("No travelers")
                : tn("1 traveler", "{n} travelers", row.pax, { n: row.pax })}
            </span>
          </li>
        ))}
      </ul>
      <div className={styles.total}>
        <span>{t("Total")}</span>
        <span className={styles.totalPax}>
          {tn("1 traveler", "{n} travelers", total, { n: total })}
        </span>
      </div>
    </div>
  );
}
